import { createClient } from '@supabase/supabase-js';
import type { Database } from '../types/database.types';

// Get Supabase URL and anon key from environment variables
const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

// Improved URL validation function
export const isValidUrl = (url: string): boolean => {
  try {
    new URL(url);
    return true;
  } catch {
    return false;
  }
};

// Detailed debugging information
// Only log configuration debug info once
let hasLoggedConfig = false;
if (!hasLoggedConfig) {
  console.log('🔍 Supabase Configuration Debug:');
  console.log('  VITE_SUPABASE_URL:', supabaseUrl ? '✅ Present' : '❌ Missing');
  console.log('  VITE_SUPABASE_ANON_KEY:', supabaseAnonKey ? '✅ Present' : '❌ Missing');
  console.log('  Actual URL value:', supabaseUrl);
  console.log('  Actual ANON_KEY value (first 20 chars):', supabaseAnonKey ? supabaseAnonKey.substring(0, 20) + '...' : 'MISSING');

  if (supabaseUrl) {
    console.log('  URL Format Valid:', isValidUrl(supabaseUrl) ? '✅ Valid' : '❌ Invalid');
    if (!isValidUrl(supabaseUrl)) {
      console.log('  Current URL Value:', supabaseUrl);
    }
  }
  hasLoggedConfig = true;
}

// Helper function to check if Supabase is properly configured
export const isSupabaseConfigured = (): boolean => {
  const hasUrl = !!supabaseUrl;
  const hasKey = !!supabaseAnonKey;
  const isUrlValid = hasUrl && isValidUrl(supabaseUrl);
  
  // Only log status occasionally to reduce console noise
  if (Math.random() < 0.1) { // 10% chance to log
    console.log(`Supabase Configuration Status: ${hasUrl && hasKey && isUrlValid ? 'OK' : 'Issues Detected'}`);
  }
  
  return hasUrl && hasKey && isUrlValid;
};

// Only create client if we have valid configuration
// Create with explicit schema setting to ensure we're looking in the right place
export const supabase = isSupabaseConfigured() ? createClient(supabaseUrl!, supabaseAnonKey!, {
  auth: {
    persistSession: true,
    autoRefreshToken: true,
    detectSessionInUrl: true,
    flowType: 'pkce',
    storageKey: 'supabase.auth.token',
    storage: window.localStorage,
    debug: false,
  },
  db: {
    schema: 'public'
  },
  realtime: {
    params: {
      eventsPerSecond: 2,
    },
  },
  global: {
    headers: {
      'x-client-info': 'future-lens/0.1.0',
    },
    fetch: async (url, options = {}) => {
      const maxRetries = 3;
      let lastError: Error | null = null;

      for (let attempt = 1; attempt <= maxRetries; attempt++) {
        try {
          const response = await fetch(url, options);

          if (response.status === 401 && attempt < maxRetries) {
            console.log(`🔄 Supabase: 401 error on attempt ${attempt}, retrying...`);
            await new Promise(resolve => setTimeout(resolve, Math.pow(2, attempt) * 100));
            continue;
          }

          return response;
        } catch (error) {
          lastError = error as Error;
          console.error(`❌ Supabase: Fetch error on attempt ${attempt}:`, error);

          if (attempt < maxRetries) {
            await new Promise(resolve => setTimeout(resolve, Math.pow(2, attempt) * 100));
          }
        }
      }

      if (lastError) {
        throw lastError;
      }

      return fetch(url, options);
    },
  },
}) : {} as any;

// Log the final client configuration for debugging
console.log('🔧 Supabase Client Created with:');
console.log('  URL:', supabaseUrl);
console.log('  Key configured:', !!supabaseAnonKey);
console.log('  Headers configured:', !!supabaseAnonKey);

// Add a test function to directly verify database access
export const testDatabaseConnection = async (): Promise<{ success: boolean; error?: string; details?: any }> => {
  try {
    // Add timeout protection for database tests
    const controller = new AbortController();
    const timeoutId = setTimeout(() => {
      controller.abort();
    }, 5000); // 5 second timeout for database tests
    
    let timeoutCleared = false;
    const clearTimeoutSafely = () => {
      if (!timeoutCleared) {
        clearTimeout(timeoutId);
        timeoutCleared = true;
      }
    };
    
    // Try to count profiles
    const { count, error } = await supabase
      .from('profiles')
      .select('*', { count: 'exact', head: true });
    
    if (error) {
      console.error('Database connection test failed:', error);
      clearTimeoutSafely();
      
      // Check for common schema issues
      if (error.message.includes('relation "profiles" does not exist')) {
        try {
          // Try checking with schema prefix
          const { data: schemaTest, error: schemaError } = await supabase.rpc('test_schema_access');
          if (schemaError) {
            clearTimeoutSafely();
            return { 
              success: false, 
              error: 'Schema access issue: ' + schemaError.message,
              details: { originalError: error, schemaTest: schemaError }
            };
          }
          return { 
            success: false, 
            error: 'Schema visibility issue, but RPC functions work',
            details: { originalError: error, schemaTest }
          };
        } catch (schemaTestError) {
          clearTimeoutSafely();
          return { 
            success: false, 
            error: 'Schema issue detected. Try invoking the fix-schema-issues function.',
            details: { originalError: error, schemaTestError }
          };
        }
      }
      
      clearTimeoutSafely();
      return { success: false, error: error.message, details: error };
    }
    
    clearTimeoutSafely();
    return { 
      success: true, 
      details: { count, schema: 'public' }
    };
  } catch (err) {
    console.error('Exception testing database connection:', err);
    return { 
      success: false, 
      error: err instanceof Error ? err.message : 'Unknown error',
      details: err
    };
  }
};
